<script setup lang="ts">
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'reka-ui'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import AppIcon from '@/components/common/AppIcon.vue'
import UserAvatar from '@/components/common/UserAvatar.vue'
import { signOut } from '@/services/session'
import { useUiStore } from '@/stores/ui'
import { useUserStore } from '@/stores/user'

const { t } = useI18n()
const user = useUserStore()
const ui = useUiStore()
const router = useRouter()

const loggingOut = ref(false)

async function handleLogout(): Promise<void> {
  if (loggingOut.value) return
  loggingOut.value = true
  try {
    await signOut()
    ui.notifySuccess(t('auth.logout.success'))
  } finally {
    loggingOut.value = false
    // Spec: logging out lands the visitor back on the guest landing page.
    await router.push({ name: 'home' })
  }
}
</script>

<template>
  <DropdownMenuRoot>
    <DropdownMenuTrigger
      class="chrome-item cursor-pointer gap-2 pr-2 pl-1"
      :aria-label="t('header.userMenu')"
    >
      <UserAvatar :src="user.avatarUrl" :initials="user.initials" size-class="h-8 w-8 text-xs" />
      <!-- Hidden again in the md..lg band: that's where the row also gains
           the group/server selectors, and the full name is the item this
           project can most afford to drop first (see AppHeader overflow bug).
           Below md there's no group/server in this row, so there's room to
           show it from sm; ≥lg the sidebar is docked and room returns. -->
      <span class="hidden max-w-32 truncate text-sm font-semibold sm:inline md:hidden lg:inline">
        {{ user.displayName }}
      </span>
      <AppIcon name="mdi-chevron-down" class="text-base opacity-70" />
    </DropdownMenuTrigger>

    <DropdownMenuPortal>
      <DropdownMenuContent
        :side-offset="10"
        align="end"
        class="panel z-50 min-w-56 p-1 shadow-raised"
      >
        <!-- Identity recap, not interactive. -->
        <div class="px-2 py-2">
          <p class="text-abyss-900 truncate text-sm font-semibold">{{ user.displayName }}</p>
          <p class="text-abyss-800/60 truncate text-xs">{{ user.user?.email }}</p>
        </div>
        <DropdownMenuSeparator class="bg-parchment-300 my-1 h-px" />

        <DropdownMenuItem
          class="text-abyss-900 flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm outline-none data-highlighted:bg-primary-50"
          @select="router.push({ name: 'profile' })"
        >
          <AppIcon name="mdi-account-outline" />
          <span>{{ t('common.profile') }}</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          class="text-abyss-900 flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm outline-none data-highlighted:bg-primary-50"
          @select="router.push({ name: 'account-settings' })"
        >
          <AppIcon name="mdi-cog-outline" />
          <span>{{ t('common.accountSettings') }}</span>
        </DropdownMenuItem>

        <!-- Only door into the panel: shown to admins and editors alike, since
             both may open it (the sections inside are what differ). -->
        <template v-if="user.isStaff">
          <DropdownMenuSeparator class="bg-parchment-300 my-1 h-px" />
          <DropdownMenuItem
            class="text-abyss-900 data-highlighted:bg-primary-50 flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm outline-none"
            @select="router.push({ name: 'admin-home' })"
          >
            <AppIcon name="mdi-shield-crown-outline" class="text-gold-600" />
            <span>{{ t('admin.nav.openPanel') }}</span>
          </DropdownMenuItem>
        </template>

        <DropdownMenuSeparator class="bg-parchment-300 my-1 h-px" />

        <DropdownMenuItem
          class="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-red-700 outline-none data-highlighted:bg-red-50"
          :data-disabled="loggingOut ? '' : undefined"
          @select="handleLogout"
        >
          <AppIcon name="mdi-logout" />
          <span>{{ t('nav.logout') }}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>
